import React, { useState, useEffect, useRef, useCallback } from 'react';
import QuickCopyPlugin from './QuickCopyPlugin';
import { Tooltip } from '../common';

const BUTTON_SIZE = 50;
const PANEL_WIDTH = 350;
const PANEL_HEIGHT = 500;
const PANEL_GAP = 10; // Gap between button and panel
const MIN_MARGIN = 10; // Minimum margin from screen edges

const FloatingTool = () => {
    const [expanded, setExpanded] = useState(false);
    const [activePlugin, setActivePlugin] = useState('quick_copy'); // 'quick_copy' | 'settings' | ...
    const [position, setPosition] = useState({ right: 20, bottom: 20 });

    const isDragging = useRef(false);
    const dragStartPos = useRef({ x: 0, y: 0 });
    const startPosition = useRef({ right: 0, bottom: 0 });

    // Prevent click when dragging
    const hasMoved = useRef(false);

    // Clamp position to keep button within viewport
    const clampPosition = useCallback((pos) => {
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;

        const maxRight = viewportWidth - BUTTON_SIZE - MIN_MARGIN;
        const maxBottom = viewportHeight - BUTTON_SIZE - MIN_MARGIN;

        return {
            right: Math.max(MIN_MARGIN, Math.min(pos.right, maxRight)),
            bottom: Math.max(MIN_MARGIN, Math.min(pos.bottom, maxBottom))
        };
    }, []);

    // Calculate panel position to keep it within viewport
    const getPanelPosition = useCallback(() => {
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;

        // Default: panel appears above the button
        let panelRight = position.right;
        let panelBottom = position.bottom + BUTTON_SIZE + PANEL_GAP;

        // Check if panel would overflow on the right
        const panelLeft = viewportWidth - panelRight - PANEL_WIDTH;
        if (panelLeft < MIN_MARGIN) {
            panelRight = viewportWidth - PANEL_WIDTH - MIN_MARGIN;
        }
        // Check if panel would overflow on the left (right value too large)
        if (panelRight < MIN_MARGIN) {
            panelRight = MIN_MARGIN;
        }

        // Check if panel would overflow at the top
        if (panelBottom + PANEL_HEIGHT > viewportHeight - MIN_MARGIN) {
            // Try placing panel below the button instead
            const bottomSpace = position.bottom - PANEL_GAP - PANEL_HEIGHT;
            if (bottomSpace >= MIN_MARGIN) {
                panelBottom = position.bottom - PANEL_GAP - PANEL_HEIGHT;
            } else {
                // Not enough space above or below, clamp to visible area
                panelBottom = Math.max(MIN_MARGIN, viewportHeight - PANEL_HEIGHT - MIN_MARGIN);
            }
        }

        // Ensure panel doesn't go below viewport
        if (panelBottom < MIN_MARGIN) {
            panelBottom = MIN_MARGIN;
        }

        return { right: panelRight, bottom: panelBottom };
    }, [position]);

    const toggleExpand = () => {
        if (!hasMoved.current) {
            setExpanded(!expanded);
        }
    };

    const handleMouseDown = (e) => {
        isDragging.current = true;
        hasMoved.current = false;
        dragStartPos.current = { x: e.clientX, y: e.clientY };
        startPosition.current = { ...position };

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
    };

    const handleMouseMove = (e) => {
        if (!isDragging.current) return;

        const dx = e.clientX - dragStartPos.current.x;
        const dy = e.clientY - dragStartPos.current.y;

        if (Math.abs(dx) > 5 || Math.abs(dy) > 5) {
            hasMoved.current = true;
        }

        // Update position with boundary clamping
        const newPosition = {
            right: startPosition.current.right - dx,
            bottom: startPosition.current.bottom - dy
        };

        setPosition(clampPosition(newPosition));
    };

    const handleMouseUp = () => {
        isDragging.current = false;
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
    };

    // Handle window resize to keep button in viewport
    useEffect(() => {
        const handleResize = () => {
            setPosition(prev => clampPosition(prev));
        };

        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, [clampPosition]);

    // Clean up drag listeners
    useEffect(() => {
        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, []);

    return (
        <>
            {/* Floating Button */}
            <div
                onMouseDown={handleMouseDown}
                onClick={toggleExpand}
                style={{
                    position: 'fixed',
                    right: `${position.right}px`,
                    bottom: `${position.bottom}px`,
                    width: '50px',
                    height: '50px',
                    borderRadius: '50%',
                    background: '#1890ff',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    cursor: isDragging.current ? 'grabbing' : 'pointer',
                    zIndex: 2999,
                    color: 'white',
                    fontSize: '24px',
                    transition: isDragging.current ? 'none' : 'background 0.3s',
                    userSelect: 'none'
                }}
                title="按住可拖动"
            >
                {expanded ? '−' : '+'}
            </div>

            {/* Expanded Widget - Dynamically positioned to stay in viewport */}
            {expanded && (() => {
                const panelPos = getPanelPosition();
                return (
                    <div
                        style={{
                            position: 'fixed',
                            right: `${panelPos.right}px`,
                            bottom: `${panelPos.bottom}px`,
                            width: `${PANEL_WIDTH}px`,
                            height: `${PANEL_HEIGHT}px`,
                            background: 'white',
                            borderRadius: '8px',
                            boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                            zIndex: 2999,
                            display: 'flex',
                            flexDirection: 'column',
                            overflow: 'hidden',
                            animation: 'slideUp 0.3s ease'
                        }}
                    >
                        {/* Header / Tabs */}
                        <div style={{ background: '#f5f5f5', padding: '10px', borderBottom: '1px solid #eee', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div style={{ display: 'flex', gap: '10px' }}>
                                <button
                                    className={`btn btn-small ${activePlugin === 'quick_copy' ? 'btn-primary' : 'btn-default'}`}
                                    onClick={() => setActivePlugin('quick_copy')}
                                >
                                    快捷复制
                                </button>
                                {/* Placeholder for future plugins */}
                                {/* <button className="btn btn-small btn-default">其他</button> */}
                            </div>
                            <div style={{ fontSize: '12px', color: '#999' }}>工具箱</div>
                        </div>

                        {/* Body */}
                        <div style={{ flex: 1, overflow: 'hidden' }}>
                            {activePlugin === 'quick_copy' && <QuickCopyPlugin />}
                        </div>
                    </div>
                );
            })()}
        </>
    );
};

export default FloatingTool;
